/**
 * Stable identifier derivation.
 *
 * The canonical model forbids deriving an id from a mutable source value, but a
 * *fixture* has the opposite problem: if `PRD-CDR` gets a fresh UUID on every
 * build, then every bookmark, screenshot, Storybook story and Playwright
 * selector in the repository rots overnight. So the seed derives RFC-4122 v5
 * UUIDs from a fixed namespace plus the immutable business code. Real ingestion
 * mints ids centrally; this file exists so the demo is quotable.
 */

/** Minimal SHA-1. Present because UUID v5 is specified in terms of it and we
 * cannot reach for `node:crypto` — the library half of this package must stay
 * runtime-agnostic so the browser fixture build can import it. */
function sha1(bytes: Uint8Array): Uint8Array {
  const ml = bytes.length * 8;
  const withPadding = new Uint8Array((((bytes.length + 8) >> 6) + 1) * 64);
  withPadding.set(bytes);
  withPadding[bytes.length] = 0x80;
  const view = new DataView(withPadding.buffer);
  view.setUint32(withPadding.length - 4, ml >>> 0, false);
  view.setUint32(withPadding.length - 8, Math.floor(ml / 4294967296), false);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const w = new Int32Array(80);

  for (let offset = 0; offset < withPadding.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getInt32(offset + i * 4, false);
    for (let i = 16; i < 80; i += 1) {
      const x = (w[i - 3] ?? 0) ^ (w[i - 8] ?? 0) ^ (w[i - 14] ?? 0) ^ (w[i - 16] ?? 0);
      w[i] = (x << 1) | (x >>> 31);
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let i = 0; i < 80; i += 1) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (((a << 5) | (a >>> 27)) + f + e + k + (w[i] ?? 0)) | 0;
      e = d;
      d = c;
      c = (b << 30) | (b >>> 2);
      b = a;
      a = temp;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  outView.setInt32(0, h0, false);
  outView.setInt32(4, h1, false);
  outView.setInt32(8, h2, false);
  outView.setInt32(12, h3, false);
  outView.setInt32(16, h4, false);
  return out;
}

function parseUuid(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function utf8(input: string): Uint8Array {
  const out: number[] = [];
  for (const char of input) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 63));
    else if (code < 0x10000)
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 63), 0x80 | (code & 63));
    else
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 63),
        0x80 | ((code >> 6) & 63),
        0x80 | (code & 63),
      );
  }
  return Uint8Array.from(out);
}

/** Root namespace for the Aurora Airlines tenant. Arbitrary but frozen. */
export const AURORA_NAMESPACE = '6f0d3c1e-9a7b-4c2d-8e5f-1b3a7d904c62';

export function uuidV5(name: string, namespace: string = AURORA_NAMESPACE): string {
  const ns = parseUuid(namespace);
  const nameBytes = utf8(name);
  const input = new Uint8Array(ns.length + nameBytes.length);
  input.set(ns);
  input.set(nameBytes, ns.length);

  const hash = sha1(input);
  const bytes = hash.slice(0, 16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x50; // version 5
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // RFC-4122 variant

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * Derive an id from an entity type and a business code. Keying on the type as
 * well as the code means `PRD-BOOK` the product and `PRD-BOOK` the assessment
 * of that product never collide.
 */
export function idFor(entityType: string, businessCode: string): string {
  return uuidV5(`${entityType}:${businessCode}`);
}

/** Correlation ids for the fixture responses. Deterministic, not random. */
export function correlationIdFor(route: string, userCode: string, sequence: number): string {
  return `cid-${uuidV5(`correlation:${route}:${userCode}:${sequence}`).slice(0, 18)}`;
}
